/*
 * MMG-4335: deficiencies carried over from the last inspection (or added here by hand on a job that
 * had no inspection, MMG-4337). Ticking a row records that the repair addressed it; any row left
 * unticked fails the asset when the job closes.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import CreatableSelect from 'react-select/creatable';

import {
    addComplianceCheckListItem,
    deleteComplianceCheckListItem,
    setComplianceCheckListVerified,
    updateComplianceCheckListName
} from '../../actions/complianceCheckListActions';
import { IinitialState } from '../../reducers';
import { selectComplianceCheckListForAsset } from '../../reducers/complianceCheckListReducer';
import { selectIsMPFormLocked } from '../../reducers/commonSelectors';
import { selectDeficiencyCodesByStandardID } from '../../reducers/manageJobCommentsReducer';
import { selectSelectedJob } from '../../reducers/manageJobReducer';
import { initialFacility } from '../../reducers/initialState';
import {
    buildDeficiencySuggestions,
    canEditDeficiency,
    IdeficiencySuggestion,
    isDeficiencyChecklistJob,
    isManualDeficiency,
    matchesDeficiencySearch,
    resolveDeficiencyName
} from './deficiencySuggestions.helpers';

interface Iprops {
    jobID: string;
    installBaseID: string;
}

export const DeficiencyChecklist = ({ jobID, installBaseID }: Iprops) => {
    const { t } = useTranslation('measurementPoints');
    const dispatch = useDispatch();
    const selectedJob = useSelector(selectSelectedJob);
    const items = useSelector(
        (state: IinitialState) => selectComplianceCheckListForAsset(state, jobID, installBaseID),
        shallowEqual
    );
    const [typedDeficiency, setTypedDeficiency] = React.useState('');
    const [editingID, setEditingID] = React.useState<string | null>(null);
    const [editingInput, setEditingInput] = React.useState('');
    const editingInputRef = React.useRef<HTMLInputElement | null>(null);
    const facility = useSelector(
        (state: IinitialState) => state.facilities.facilitiesByID[selectedJob.facilityID] || initialFacility
    );
    const standardName = useSelector(
        (state: IinitialState) => (state.productInfo.standards[facility.standardID] || { name: '' }).name
    );
    const codesByID = useSelector(
        (state: IinitialState) =>
            selectDeficiencyCodesByStandardID(state.manageJobComment.deficiencyCodesByID, facility.standardID),
        shallowEqual
    );
    const suggestions = React.useMemo(() => buildDeficiencySuggestions(codesByID, standardName), [
        codesByID,
        standardName
    ]);
    /*
     * The checklist belongs to the job in hand, so while a past visit is on screen it is read only -
     * a tick or an add here would land on the open job, not the visit being looked at (MMG-4345). The
     * approver viewing a completed job may not change it either (MMG-4480).
     */
    const isLocked = useSelector(selectIsMPFormLocked);

    // preventScroll: the checklist sits in a scrolling tab pane, which otherwise jumps on focus
    React.useEffect(() => {
        if (editingID && editingInputRef.current) {
            editingInputRef.current.focus({ preventScroll: true });
        }
    }, [editingID]);

    // Maintenance and Warranty jobs share these question lists, so the tab flag alone cannot decide -
    // the job type does (MMG-4460: Repair only; the server rejects adds for anything else)
    if (!isDeficiencyChecklistJob(selectedJob.jobTypeID)) {
        return null;
    }

    const handleToggle = (id: string, verified: boolean) => {
        dispatch(setComplianceCheckListVerified(id, verified));
    };

    const pendingName = resolveDeficiencyName(typedDeficiency);

    const addDeficiency = (name: string) => {
        if (!name) {
            return;
        }

        dispatch(addComplianceCheckListItem(jobID, installBaseID, name.slice(0, 500)));
        setTypedDeficiency('');
    };

    const handleAdd = () => addDeficiency(pendingName);

    const startEditing = (id: string, name: string) => {
        setEditingID(id);
        setEditingInput(name);
    };

    const cancelEditing = () => {
        setEditingID(null);
        setEditingInput('');
    };

    const saveEditing = (id: string, originalName: string) => {
        const next = editingInput.trim();
        if (!next || next === originalName) {
            cancelEditing();
            return;
        }

        dispatch(updateComplianceCheckListName(id, next));
        cancelEditing();
    };

    const handleDelete = (id: string) => {
        dispatch(deleteComplianceCheckListItem(id, t));
    };

    const formatSuggestion = (suggestion: IdeficiencySuggestion, meta: { context: string }) => {
        if (meta.context === 'value' || !suggestion.detail) {
            return <span>{suggestion.label}</span>;
        }

        return (
            <div className="deficiency-checklist-option">
                <strong>{suggestion.label}</strong>
                {suggestion.standardName && (
                    <span className="deficiency-checklist-option-standard">{suggestion.standardName}</span>
                )}
                <span className="deficiency-checklist-option-detail">{suggestion.detail}</span>
            </div>
        );
    };

    const selectProps = {
        className: 'deficiency-checklist-select beacon-select',
        classNamePrefix: 'react-select',
        options: suggestions,
        filterOption: (option: { data: unknown }, input: string) =>
            matchesDeficiencySearch(option.data as IdeficiencySuggestion, input),
        formatOptionLabel: (data: unknown, meta: { context: string }) =>
            formatSuggestion(data as IdeficiencySuggestion, meta),
        formatCreateLabel: (input: string) => input,
        noOptionsMessage: () => null,
        isClearable: true,
        blurInputOnSelect: false,
        menuPortalTarget: document.body,
        menuPosition: 'fixed' as const,
        styles: { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }
    };

    return (
        <div className="deficiency-checklist">
            <h4 className="deficiency-checklist-title">{t('measurementPoints:deficiencyChecklistTitle')}</h4>
            <p className="deficiency-checklist-intro">
                {items.length
                    ? t('measurementPoints:deficiencyChecklistIntro')
                    : t('measurementPoints:deficiencyChecklistEmptyIntro')}
            </p>

            {items.map(item => {
                const canEdit = canEditDeficiency({ isManual: isManualDeficiency(item), isHistoricalMode: isLocked });

                if (canEdit && editingID === item.id) {
                    return (
                        <div className="deficiency-checklist-item deficiency-checklist-item-editing" key={item.id}>
                            <input
                                type="text"
                                ref={editingInputRef}
                                aria-label={item.name}
                                className="form-control deficiency-checklist-edit-input"
                                value={editingInput}
                                maxLength={500}
                                onChange={event => setEditingInput(event.target.value)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        saveEditing(item.id, item.name);
                                    }
                                    if (event.key === 'Escape') {
                                        cancelEditing();
                                    }
                                }}
                            />
                            <span className="deficiency-checklist-item-actions">
                                <button
                                    type="button"
                                    className="deficiency-checklist-action deficiency-checklist-action-save"
                                    aria-label={t('common:save')}
                                    title={t('common:save')}
                                    disabled={!editingInput.trim() || editingInput.trim() === item.name}
                                    onClick={() => saveEditing(item.id, item.name)}
                                >
                                    <FontAwesomeIcon icon={['far', 'check']} />
                                </button>
                                <button
                                    type="button"
                                    className="deficiency-checklist-action"
                                    aria-label={t('common:cancel')}
                                    title={t('common:cancel')}
                                    onClick={cancelEditing}
                                >
                                    <FontAwesomeIcon icon={['far', 'times']} />
                                </button>
                            </span>
                        </div>
                    );
                }

                return (
                    <div className="deficiency-checklist-item" key={item.id}>
                        <label>
                            <input
                                type="checkbox"
                                checked={item.verified}
                                disabled={isLocked}
                                onChange={event => handleToggle(item.id, event.target.checked)}
                            />
                            <span>{item.name}</span>
                        </label>
                        {canEdit && (
                            <span className="deficiency-checklist-item-actions">
                                <button
                                    type="button"
                                    className="deficiency-checklist-action"
                                    aria-label={t('common:edit')}
                                    title={t('common:edit')}
                                    onClick={() => startEditing(item.id, item.name)}
                                >
                                    <FontAwesomeIcon icon={['far', 'edit']} />
                                </button>
                                <button
                                    type="button"
                                    className="deficiency-checklist-action deficiency-checklist-action-delete"
                                    aria-label={t('common:delete')}
                                    title={t('common:delete')}
                                    onClick={() => handleDelete(item.id)}
                                >
                                    <FontAwesomeIcon icon={['far', 'trash-alt']} />
                                </button>
                            </span>
                        )}
                    </div>
                );
            })}

            {!isLocked && (
                <div className="deficiency-checklist-add">
                    <CreatableSelect
                        {...selectProps}
                        value={null}
                        inputValue={typedDeficiency}
                        /* picking a suggestion adds it straight away, the same as typing one and pressing enter */
                        onChange={suggestion => {
                            const picked = suggestion as IdeficiencySuggestion | null;
                            setTypedDeficiency('');
                            if (picked) {
                                addDeficiency(picked.label);
                            }
                        }}
                        onInputChange={(input, meta) => {
                            // a blur or menu close must not wipe free text the engineer has not added yet
                            if (meta.action === 'input-change') {
                                setTypedDeficiency(input);
                            }
                        }}
                        onCreateOption={input => addDeficiency(input.trim())}
                        placeholder={t('measurementPoints:deficiencyChecklistAddPlaceholder')}
                    />
                    <button type="button" className="btn btn-link" onClick={handleAdd} disabled={!pendingName}>
                        + {t('measurementPoints:deficiencyChecklistAdd')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default DeficiencyChecklist;
